import { useMemo, useState } from "react";
import { Icon, Reveal, SERIF } from "./ui";
import { formatPressDate } from "./data";

/* --------------------------- SORT TOGGLE --------------------------- */

function SortToggle({ order, setOrder }) {
  return (
    <div className="flex items-center gap-2">
      <Icon name="sort" className="h-4 w-4 text-stone-500" strokeWidth={2} />
      <div className="flex overflow-hidden rounded-full border border-stone-300 bg-white shadow-sm">
        {[
          { key: "new", label: "Newest first" },
          { key: "old", label: "Oldest first" },
        ].map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => setOrder(o.key)}
            aria-pressed={order === o.key}
            className={`px-3.5 py-1.5 text-[11.5px] font-extrabold transition-all duration-300 sm:px-4 sm:text-xs ${
              order === o.key
                ? "bg-[#DA2128] text-white shadow-inner"
                : "bg-white text-stone-800 hover:bg-[#FEF7F4] hover:text-[#DA2128]"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- PRESS CARD ----------------------------- */

function PressCard({ item, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <article className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-[0_1px_2px_rgba(28,25,23,.06)] ring-1 ring-stone-200/80 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-[#DA2128]/10 hover:ring-[#DA2128]/25 sm:p-7">
        <span className="absolute inset-y-0 left-0 w-1 origin-top scale-y-0 bg-gradient-to-b from-[#DA2128] to-[#F59C00] transition-transform duration-500 group-hover:scale-y-100" />

        <p className="flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.16em] text-stone-700">
          <Icon name="calendar" className="h-4 w-4 text-stone-400" strokeWidth={2} />
          {formatPressDate(item.date)}
          {item.source && (
            <>
              <span className="text-stone-300">|</span>
              <span className="text-[#DB6400]">{item.source}</span>
            </>
          )}
        </p>

        <h3
          style={SERIF}
          className="mt-3 text-[19px] font-bold leading-snug text-[#DA2128] sm:text-[21px]"
        >
          <a href="#press" className="decoration-[#F59C00] decoration-2 underline-offset-4 hover:underline">
            {item.title}
          </a>
        </h3>

        <p className="mt-2.5 text-[14px] leading-relaxed text-stone-600">{item.excerpt}</p>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {item.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#DA2128]/25 bg-[#FEF7F4] px-3 py-1 text-[11px] font-medium text-[#DA2128]"
              >
                <Icon name="tag" className="h-3 w-3" strokeWidth={2} />
                {t}
              </span>
            ))}
          </div>
          <a
            href="#press"
            className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] font-semibold text-[#DA2128] transition-colors hover:text-[#DB6400]"
          >
            Read full story
            <Icon
              name="arrow"
              className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
              strokeWidth={2.6}
            />
          </a>
        </div>
      </article>
    </Reveal>
  );
}

/* ---------------------------- PRESS COLUMN ---------------------------- */

export function PressColumn({ title, kicker, icon, items }) {
  const [order, setOrder] = useState("new");

  const sorted = useMemo(() => {
    const list = [...items];
    list.sort((a, b) =>
      order === "new"
        ? new Date(b.date) - new Date(a.date)
        : new Date(a.date) - new Date(b.date)
    );
    return list;
  }, [items, order]);

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#FEF7F4] text-[#DA2128] ring-1 ring-[#DA2128]/15">
              <Icon name={icon} className="h-5 w-5" strokeWidth={1.9} />
            </span>
            <div>
              <p className="text-[10.5px] font-extrabold uppercase tracking-[0.2em] text-[#DB6400]">
                {kicker}
              </p>
              <h3 style={SERIF} className="text-[22px] font-bold leading-tight text-stone-900">
                {title}
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Icon name="rss" className="hidden h-5 w-5 text-[#DA2128] sm:block" strokeWidth={2} />
            <SortToggle order={order} setOrder={setOrder} />
          </div>
        </div>
      </Reveal>

      <div className="mt-4 h-[2px] w-full rounded-full bg-stone-900/85" />

      <div className="mt-6 flex flex-col gap-5">
        {sorted.map((item, i) => (
          <PressCard key={`${item.title}-${item.date}`} item={item} delay={i * 70} />
        ))}
      </div>

      <div className="mt-6">
        <a
          href="#press"
          className="group inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-[12.5px] font-extrabold uppercase tracking-[0.12em] text-stone-700 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#DA2128] hover:text-[#DA2128]"
        >
          View archive
          <Icon
            name="arrow"
            className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
            strokeWidth={2.6}
          />
        </a>
      </div>
    </div>
  );
}

export default PressColumn;
